import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import React from 'react';
import { arrayOf, bool, object, shape } from 'prop-types';
import Wrapper from '../Wrapper';
import Styled from '../../utils/Styled';
import { GuestType } from '../../utils/constants';
import FillInDetails from './FillInDetails';

const Container = Styled(Wrapper, {
  marginBottom: '12px',
});

const FillInDetailsList = ({
  guestList,
  onClick,
  type = GuestType.Adult,
  fromIndex = 0,
  startGuestIndex = 0,
  touched,
  move,
  ...props
}) => {
  const guestPrefix = translate(
    type === GuestType.Adult ? 'common.adult' : 'common.child'
  );
  const getGuestPrefixWithIndex = index =>
    translate('tripDetails.guestIndex', {
      guest: guestPrefix,
      count: index + startGuestIndex + 1,
    });

  const handleOnClick = index => () => {
    onClick &&
      onClick({
        guestPrefix,
        count: index + startGuestIndex + 1,
        index: index + fromIndex,
        type,
      });
  };

  return (
    guestList.length > 0 &&
    guestList.map((guest, index) => (
      <Container className="guest" key={index}>
        <FillInDetails
          id={`${type}_${index + startGuestIndex + 1}`}
          placeholderPrefix={getGuestPrefixWithIndex(index)}
          onClick={handleOnClick(index)}
          onEdit={handleOnClick(index)}
          error={touched && !guest}
          content={
            guest && {
              prefix: getGuestPrefixWithIndex(index),
              primary: `${guest.givenName} ${guest.familyName}`,
              secondary: guest.dateOfBirth,
            }
          }
          move={move}
          {...props}
        />
      </Container>
    ))
  );
};

const GuestFillInDetails = ({ guestDetails, move, ...props }) => {
  const { adults, children, infants } = guestDetails;

  return (
    <>
      <Wrapper id="guest-details-adults">
        <FillInDetailsList guestList={adults} adult move={move} {...props} />
      </Wrapper>
      <Wrapper id="guest-details-children">
        <FillInDetailsList
          guestList={children}
          type={GuestType.Children}
          move={move}
          {...props}
        />
      </Wrapper>
      <Wrapper id="guest-details-infants">
        <FillInDetailsList
          guestList={infants}
          startGuestIndex={children.length}
          type={GuestType.Infants}
          move={move}
          {...props}
        />
      </Wrapper>
    </>
  );
};

const MultRoomGuestFillInDetails = ({ guestDetails, move, ...props }) => {
  const { adults, children, infants } = guestDetails;

  return (
    <>
      <Wrapper id="guest-details-adults">
        <FillInDetailsList
          fromIndex={adults.fromIndex}
          startGuestIndex={adults.startGuestIndex}
          guestList={adults.guestList}
          adult
          move={move}
          {...props}
        />
      </Wrapper>
      <Wrapper id="guest-details-children">
        <FillInDetailsList
          fromIndex={children.fromIndex}
          startGuestIndex={children.startGuestIndex}
          guestList={children.guestList}
          type={GuestType.Children}
          move={move}
          {...props}
        />
      </Wrapper>
      <Wrapper id="guest-details-infants">
        <FillInDetailsList
          fromIndex={infants.fromIndex}
          startGuestIndex={infants.startGuestIndex}
          guestList={infants.guestList}
          type={GuestType.Infants}
          move={move}
          {...props}
        />
      </Wrapper>
    </>
  );
};

GuestFillInDetails.propTypes = {
  guestDetails: shape({
    adults: arrayOf(object),
    children: arrayOf(object),
    infants: arrayOf(object),
  }).isRequired,
  move: bool,
};

MultRoomGuestFillInDetails.propTypes = {
  guestDetails: shape({
    adults: arrayOf(object),
    children: arrayOf(object),
    infants: arrayOf(object),
  }).isRequired,
  move: bool,
};

export { GuestFillInDetails, MultRoomGuestFillInDetails };
